<script lang="ts">
	import { PUBLIC_BASE_URL } from '$env/static/public';
  import { browser } from "$app/environment";
  import { GtmStore } from '$lib/utils/gtm-store';
	import { FilterStore } from '$lib/utils/filters/store';
  import { StructureUtils } from '$lib/utils/structured-utils';
	import type { IStaticPage, INews } from "$lib/utils/contentful/types";
  import type { PageData } from "./$types";
  // Components
  import { Head } from '$lib/components/atoms/Head'
  import { Breadcrumb, BreadcrumbItem } from '$lib/components/molecules/Breadcrumb';
  import { Grid } from '$lib/components/organisms/Layout';
  import { ArticleCard, ArticleCardWide } from '$lib/components/organisms/Card';
  import { PromoHook, Section, SimpleCardsGrid } from '$lib/components/organisms/Section';
	import { LoadMore } from '$lib/components/atoms/LoadMore';
	import { useNewsUtils } from '$lib/utils/news-utils';
	import { FiltersNews } from '$lib/components/molecules/Filter';
	import type { INewsFilters } from '$lib/utils/news-utils/types';

  export let data: PageData;

  const mostReadNews = data.settingsData.mostReadNews || [];
  const promoBox = data.settingsData.newsListingPromoBox || null;

  const filterStoreInstance = FilterStore.getInstance();
  const filters = filterStoreInstance.filterStore; // FIXME: maybe we need a separate store?
  const isAnyFilterApplied = filterStoreInstance.isAnyFilterApplied;

  // Simulate pagination
  let newsList: INews[] = [];
  let page = 0;
  const pageSize = 30;
  let hasMore = true;
  let isLoading = false;

  let lastNews: INews | null = null;

  const newsUtils = useNewsUtils(fetch);

  const getLastNews = async () => {
    lastNews = await newsUtils.getLastNews();
  };

  const loadMoreNews = async (filters : INewsFilters | null = null) => {
    isLoading = true;
    try {
      const { news, total } = await newsUtils.getNews(page, pageSize, filters, 'publishedDate', [`${lastNews?.sys.id}`]);
      
      newsList = [...newsList, ...news];
      
      if (newsList.length < total) {
        hasMore = true;
      } else {
        hasMore = false;
      }
      page++;
    } catch (e) {
      hasMore = false;
    } finally {
      isLoading = false;
    }
  };

  const applyFilter = () => {
    isLoading = true;
    page = 0;
    newsList = [];

    if ((!$filters.category || $filters.category.length === 0) && (!$filters.month || $filters.month.length === 0)) {
      loadMoreNews(null);
    } else {
      loadMoreNews($filters);
    }
  };

  // Load first events on startup
  getLastNews();
  $: if (lastNews) {
    loadMoreNews();
  }

  /******************** GTM EVENT PUSH *************************/
  GtmStore.pushEvent({
    pageCategory: 'News',
  });
  /*************************************************************/

  const breadcrumbJson = StructureUtils.getBreadcrumbStructure(
    [
      {
        name: 'Home',
        url: PUBLIC_BASE_URL,
      },
      {
        name: 'News',
        url: `${PUBLIC_BASE_URL}news`,
      },
    ],
    true
  );

  let ldScriptTag = '';
  ldScriptTag = StructureUtils.createScriptTag(JSON.stringify([breadcrumbJson]));

  const metaTagData: IStaticPage | null = data?.metaTagData ? JSON.parse(data.metaTagData) : null;

  const seoDetails = {
    title: metaTagData?.fields?.metaTagSocialTitle || 'News',
    description: metaTagData?.fields?.metaTagSocialDescription || 'Scopri tutte le news di 24ORE Business School e registrati alla nostra Newsletter per rimanre aggiornato.',
    metaTagSocialTitle: metaTagData?.fields?.metaTagSocialTitle || '',
    metaTagSocialDescription: metaTagData?.fields?.metaTagSocialDescription || '',
    url: '/news',
    image: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
    metaTagSocialImage: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
    ldScriptTag: ldScriptTag,
  };
</script>

<Head appendCompany {seoDetails} />

<Breadcrumb disableLastElementLink>
  <BreadcrumbItem>
    <a href="/">Home</a>
  </BreadcrumbItem>
  <BreadcrumbItem current>
    <a href="/news">News</a>
  </BreadcrumbItem>
</Breadcrumb>

{#if lastNews}
  <ArticleCardWide bind:article={lastNews} />
{/if}

<!-- filter -->
<FiltersNews {applyFilter} {isLoading}/>

<Section compact class="bg-gray-50">
  <svelte:fragment slot="header">
    <h2 class="fs-3">{$isAnyFilterApplied ? 'News più pertinenti' : 'Ultime news'}</h2>
  </svelte:fragment>
  
  <Grid noGrid>
    <div class="space-y-6 md:space-y-16">
      <div class="header-results">
        {#if !isLoading}
          {#if newsList.length > 0}
            <p class="section-results">
              {`${newsList.length} risultat${newsList.length === 1 ? 'o' : 'i'}`}
            </p>
          {:else}
            <p class="section-no-results">Non ci sono news che soddisfano la tua ricerca, prova a cambiare i filtri</p>
          {/if}
        {/if}
      </div>
    </div>
  </Grid>

  {#if newsList.length > 0}
    <!-- news section 1 -->
    <Grid>
      <SimpleCardsGrid>
        {#each newsList.slice(0, 6) as article}
          <ArticleCard {article} />
        {/each}
      </SimpleCardsGrid>
    </Grid>
  {/if}

  <!-- most read section -->
  {#if mostReadNews.length > 0}
    <Section compact class="bg-petrol my-6">
      <Grid noGrid>
        <h2 class="fs-3 text-white mb-6">I più letti</h2>
        <SimpleCardsGrid>
          {#each mostReadNews as article}
            <ArticleCard {article} />
          {/each}
        </SimpleCardsGrid>
      </Grid>
    </Section>
  {/if}

  {#if newsList.length > 6}
    {#if !$isAnyFilterApplied}
      <!-- news section 2 -->
      <Grid>
        <SimpleCardsGrid>
          {#each newsList.slice(6, 12) as article}
            <ArticleCard {article} />
          {/each}
        </SimpleCardsGrid>
      </Grid>

      <!-- banner -->
      {#if promoBox}
        <div class="my-6">
          <PromoHook data={promoBox.fields} />
        </div>
      {/if}

      {#if newsList.length > 12}
        <!-- news section 3 -->
        <Grid>
          <SimpleCardsGrid>
            {#each newsList.slice(12) as article}
              <ArticleCard {article} />
            {/each}
          </SimpleCardsGrid>
        </Grid>
      {/if}
    {:else}
      <!-- news section 2 -->
      <Grid>
        <SimpleCardsGrid>
          {#each newsList.slice(6) as article}
            <ArticleCard {article} />
          {/each}
        </SimpleCardsGrid>
      </Grid>
    {/if}
  {/if}

  <div class="mt-16" slot="footer">
    {#if hasMore}
      <LoadMore {isLoading} label="Altri risultati" on:click={() => loadMoreNews($isAnyFilterApplied ? $filters : null)} />
    {/if}
  </div>
</Section>

<style type="text/scss" lang="scss" scoped>
  .header-results {
    @apply flex justify-end;
    @apply my-5;
  }

  .section-results {
    @apply text-xs md:text-base text-gray-900;
  }

  .section-no-results {
    @apply md:text-2xl text-lg w-full text-center font-semibold my-6 pt-4;
  }
</style>